/**
 * Extração de Pix copia-e-cola (EMV) e linha digitável a partir de texto de PDF.
 */

const PIX_GUI = 'BR.GOV.BCB.PIX'

export function crc16CcittFalse(str) {
  let crc = 0xffff
  for (let i = 0; i < str.length; i += 1) {
    crc ^= str.charCodeAt(i) << 8
    for (let j = 0; j < 8; j += 1) {
      crc = (crc & 0x8000) ? ((crc << 1) ^ 0x1021) : (crc << 1)
      crc &= 0xffff
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0')
}

export function isValidPixPayload(payload) {
  if (!payload || payload.length < 40 || payload.length > 512) return false
  if (!payload.startsWith('000201')) return false
  if (!payload.toUpperCase().includes(PIX_GUI)) return false
  const crcTag = payload.slice(-8)
  if (!/^6304[0-9A-F]{4}$/i.test(crcTag)) return false
  const expected = crc16CcittFalse(payload.slice(0, -4))
  return expected === crcTag.slice(4).toUpperCase()
}

function compactWhitespace(text) {
  return String(text || '').replace(/\s+/g, '')
}

/** Reconstrói texto extraído caractere a caractere (espaços extras do PDF). */
export function decodeSpacedChars(text) {
  const parts = String(text || '').replace(/[\r\n\t]+/g, ' ').split(' ')
  let out = ''
  let pendingSpace = false
  parts.forEach((part) => {
    if (part === '') {
      pendingSpace = true
      return
    }
    if (pendingSpace) {
      out += ' '
      pendingSpace = false
    }
    out += part
  })
  return out
}

function pixCandidatesFrom(raw) {
  if (!raw) return []
  const found = []
  const upper = raw.toUpperCase()
  let idx = upper.indexOf('000201')
  while (idx !== -1) {
    const slice = raw.slice(idx, idx + 512)
    let searchFrom = 20
    while (searchFrom < slice.length - 7) {
      const use = slice.toUpperCase().indexOf('6304', searchFrom)
      if (use < 0) break
      const payload = slice.slice(0, use + 8)
      if (isValidPixPayload(payload)) found.push(payload)
      searchFrom = use + 4
    }
    idx = upper.indexOf('000201', idx + 1)
  }
  return found
}

/**
 * @returns {string|null} payload EMV válido
 */
export function extractPixCopiaECola(text) {
  if (!text) return null
  const variants = [
    String(text),
    String(text).replace(/[\r\n\t]+/g, ''),
    decodeSpacedChars(text),
    compactWhitespace(text),
  ]
  const candidates = []
  variants.forEach((variant) => {
    pixCandidatesFrom(variant).forEach((item) => candidates.push(item))
  })
  if (candidates.length > 0) {
    return candidates.sort((a, b) => b.length - a.length)[0]
  }

  const loose = compactWhitespace(text).match(/000201[\x21-\x7E]{30,500}?6304[0-9A-Fa-f]{4}/)
  if (loose && isValidPixPayload(loose[0])) return loose[0]
  return null
}

export function mod10(number) {
  const digits = String(number).replace(/\D/g, '')
  let sum = 0
  let multiplier = 2
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let prod = Number(digits[i]) * multiplier
    if (prod > 9) prod = Math.floor(prod / 10) + (prod % 10)
    sum += prod
    multiplier = multiplier === 2 ? 1 : 2
  }
  const rest = sum % 10
  return rest === 0 ? 0 : 10 - rest
}

export function barcode44ToLinhaDigitavel(code) {
  const c = String(code || '').replace(/\D/g, '')
  if (c.length !== 44) return null
  if (c[3] !== '9') return null
  const campo1 = c.slice(0, 4) + c.slice(19, 24)
  const campo2 = c.slice(24, 34)
  const campo3 = c.slice(34, 44)
  const campo4 = c[4]
  const campo5 = c.slice(5, 19)
  return (
    campo1 + String(mod10(campo1))
    + campo2 + String(mod10(campo2))
    + campo3 + String(mod10(campo3))
    + campo4
    + campo5
  )
}

export function isValidLinhaDigitavel47(digits) {
  const d = String(digits || '').replace(/\D/g, '')
  if (d.length !== 47) return false
  if (d[3] !== '9') return false
  const c1 = d.slice(0, 9)
  const dv1 = Number(d[9])
  const c2 = d.slice(10, 20)
  const dv2 = Number(d[20])
  const c3 = d.slice(21, 31)
  const dv3 = Number(d[31])
  return mod10(c1) === dv1 && mod10(c2) === dv2 && mod10(c3) === dv3
}

export function isValidLinhaConcessionaria48(digits) {
  const d = String(digits || '').replace(/\D/g, '')
  return d.length === 48 && d.startsWith('8')
}

const FORMATTED_47 = /(\d{5})\.(\d{5})\s+(\d{5})\.(\d{6})\s+(\d{5})\.(\d{6})\s+(\d)\s+(\d{14})/g
const FORMATTED_48 = /(\d{12})\s+(\d{12})\s+(\d{12})\s+(\d{12})/g

function collectSliding(digitStream, size, predicate) {
  const found = []
  if (digitStream.length < size) return found
  for (let i = 0; i <= digitStream.length - size; i += 1) {
    const chunk = digitStream.slice(i, i + size)
    if (predicate(chunk)) found.push(chunk)
  }
  return found
}

/**
 * @returns {string|null} somente dígitos (47 boleto ou 48 concessionária)
 */
export function extractLinhaDigitavel(text) {
  if (!text) return null

  const formatted47 = []
  String(text).replace(FORMATTED_47, (full, a, b, c, d, e, f, g, h) => {
    const digits = `${a}${b}${c}${d}${e}${f}${g}${h}`.replace(/\D/g, '')
    if (digits.length === 47 && isValidLinhaDigitavel47(digits)) formatted47.push(digits)
    return full
  })
  if (formatted47.length > 0) return formatted47[0]

  const formatted48 = []
  String(text).replace(FORMATTED_48, (full, a, b, c, d) => {
    const digits = `${a}${b}${c}${d}`
    if (isValidLinhaConcessionaria48(digits)) formatted48.push(digits)
    return full
  })
  if (formatted48.length > 0) return formatted48[0]

  const compactDigits = String(text).replace(/\D/g, '')
  const valid47 = collectSliding(compactDigits, 47, isValidLinhaDigitavel47)
  if (valid47.length > 0) return valid47[0]

  const valid48 = collectSliding(compactDigits, 48, isValidLinhaConcessionaria48)
  if (valid48.length > 0) return valid48[0]

  const valid44 = collectSliding(compactDigits, 44, (chunk) => chunk[3] === '9')
  for (let i = 0; i < valid44.length; i += 1) {
    const converted = barcode44ToLinhaDigitavel(valid44[i])
    if (converted && isValidLinhaDigitavel47(converted)) return converted
  }

  return null
}

export async function getPdfPlainText(pdf) {
  if (!pdf || !pdf.numPages) return ''
  const parts = []
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
    const page = await pdf.getPage(pageNum)
    const content = await page.getTextContent()
    const strs = (content.items || []).map((item) => item.str || '')
    parts.push(strs.join(''))
    parts.push(strs.join(' '))
  }
  return parts.join('\n')
}

function textFromLayerSpans(rootEl, region) {
  if (!rootEl) return ''
  const spans = rootEl.querySelectorAll('.react-pdf__Page__textContent span, .react-pdf__Page__textLayer span')
  if (!spans.length) {
    const sel = window.getSelection && window.getSelection()
    return sel ? String(sel.toString() || '') : ''
  }
  if (!region) {
    return Array.from(spans).map((s) => s.textContent || '').join(' ')
  }
  const hits = []
  spans.forEach((span) => {
    const r = span.getBoundingClientRect()
    const intersects = r.right >= region.left
      && r.left <= region.right
      && r.bottom >= region.top
      && r.top <= region.bottom
    if (intersects) hits.push(span.textContent || '')
  })
  return hits.join(' ')
}

export function getSelectedOrRegionText(rootEl, region) {
  const sel = window.getSelection && window.getSelection()
  const selected = sel ? String(sel.toString() || '').trim() : ''
  if (selected.length >= 8 && !region) return selected
  return textFromLayerSpans(rootEl, region)
}

export async function detectBarcodesFromCanvas(canvas, formats) {
  if (!canvas || typeof window === 'undefined' || !('BarcodeDetector' in window)) {
    return []
  }
  try {
    const detector = new window.BarcodeDetector({ formats })
    const codes = await detector.detect(canvas)
    return (codes || []).map((c) => String(c.rawValue || '')).filter(Boolean)
  } catch (err) {
    return []
  }
}

export async function renderPdfPageToCanvas(pdf, pageNumber, scale = 2) {
  const page = await pdf.getPage(pageNumber)
  const viewport = page.getViewport({ scale })
  const canvas = document.createElement('canvas')
  canvas.width = viewport.width
  canvas.height = viewport.height
  const ctx = canvas.getContext('2d')
  await page.render({ canvasContext: ctx, viewport }).promise
  return canvas
}

export function pickPixFromStrings(values) {
  for (let i = 0; i < values.length; i += 1) {
    const found = extractPixCopiaECola(values[i])
    if (found) return found
    if (isValidPixPayload(compactWhitespace(values[i]))) {
      return compactWhitespace(values[i])
    }
  }
  return null
}

export function pickLinhaFromStrings(values) {
  for (let i = 0; i < values.length; i += 1) {
    const found = extractLinhaDigitavel(values[i])
    if (found) return found
  }
  return null
}
